 import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { Page } from '../type/type';
import {agreePurchaseOrder, createBokbiDeposit, IBokbiDepositRequest} from '../service/bokbiAPI';

// 복비 히스토리 타입
export interface IBokbiHistory {
  companyName: string;
  createAt: string;
  chargeType: string;
  amount: number;
  balance: number;
  memo: string;
  franchiseChargeHstId?: string;
  purchaseOrderId?: string;
}

export interface IBokbiStatistics {
  companyName: string;
  createdAt: string;
  amount: {
    ORDER_PAY: number;
    CHARGE: number;
  };
  balance: number;
}

interface BokbiStatisticsResponse {
  content: IBokbiStatistics[];
}

// 주문 목록 응답 타입
interface BokbiHistoryResponse extends Page {
  content: IBokbiHistory[];
}

type BokbiHistoryData = {
  // 로딩
  getBokbiHistoryLoading: boolean;
  getBokbiStatisticsLoading: boolean;
  getAnnualBokbiStatisticsLoading: boolean;
  createDepositLoading: boolean;

  // 복비 히스토리 목록
  bokbiHistory: IBokbiHistory[];
  bokbiHistoryPage: Page | null;

  // 복비 통계 목록
  bokbiStatistics: IBokbiStatistics[];

  // 연간 복비 통계 목록
  annualBokbiStatistics: IBokbiStatistics[];
};

type BokbiHistoryAction = {
  getBokbiHistory: (params: {
    page?: number;
    size?: number;
    chargeType?: 'CHARGE' | 'ORDER_PAY' | 'CANCEL'
    franchiseId?: string;
    StartDt?: string;
    EndDt?: string;
  }) => void;

  getBokbiStatistics: (params: {
    franchiseId: string;
    startDt: string;
    endDt: string;
  }) => void;

  createDeposit: (data: IBokbiDepositRequest) => Promise<boolean>;
};

export const useBokbiHistoryStore = create<
  BokbiHistoryData & BokbiHistoryAction
>((set, get) => ({
  // 로딩
  getBokbiHistoryLoading: false,
  getBokbiStatisticsLoading: false,
  getAnnualBokbiStatisticsLoading: false,
  createDepositLoading: false,

  // 복비 히스토리 목록
  bokbiHistory: [],
  bokbiHistoryPage: null,

  // 복비 통계 목록
  bokbiStatistics: [],

  // 연간 복비 통계 목록
  annualBokbiStatistics: [],

  // 복비 히스토리 가져오기
  getBokbiHistory: async ({
    page,
    size,
    chargeType,
    franchiseId,
    StartDt,
    EndDt,
  }) => {
    set({ getBokbiHistoryLoading: true }); // 로딩 시작

    try {
      const response = await apiClient.get<BokbiHistoryResponse>(
        '/charge-hst',
        {
          params: {
            page,
            size,
            chargeType,
            franchiseId,
            StartDt,
            EndDt,
          },
        }
      );

      const { content, ...rest } = response.data;

      set({ bokbiHistory: content }); // 복비 내역 목록 세팅
      set({ bokbiHistoryPage: rest }); // 복비 내역 페이지 세팅
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('복비 내역 가져오지 못했습니다.');
    }

    set({ getBokbiHistoryLoading: false }); // 로딩 끝
  },

  getBokbiStatistics: async ({ franchiseId, startDt, endDt }) => {
    set({ getBokbiStatisticsLoading: true }); // 로딩 시작

    try {
      const response = await apiClient.get<BokbiStatisticsResponse>(
        '/charge-hst/stats',
        {
          params: {
            franchiseId,
            startDt,
            endDt,
          },
        }
      );

      const { content, ...rest } = response.data;

      set({ bokbiStatistics: content || [] }); // 복비 통계 목록 세팅
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('복비 통계를 가져오지 못했습니다.');
    }

    set({ getBokbiStatisticsLoading: false }); // 로딩 끝
  },

  // 복비 입금 신청
  createDeposit: async (data: IBokbiDepositRequest) => {
    set({ createDepositLoading: true });

    try {
      await createBokbiDeposit(data);
      toast.success('복비 입금 신청이 완료되었습니다.');
      
      // 복비 히스토리 새로고침
      get().getBokbiHistory({ page: 0, size: 10 });
      
      set({ createDepositLoading: false });
      return true;
    } catch (error) {
      console.error('복비 입금 신청 실패:', error);
      toast.error('복비 입금 신청에 실패했습니다.');
      set({ createDepositLoading: false });
      return false;
    }
  },

}));
